class VeloraTopBar extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.events = new AbortController();
    this.updateOffset = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        const rect = this.getBoundingClientRect();
        document.documentElement.style.setProperty('--top-bar-height', `${rect.height}px`);
        document.documentElement.style.setProperty('--top-bar-offset', `${Math.max(0, rect.bottom)}px`);
      });
    };
    window.addEventListener('scroll', this.updateOffset, { passive: true, signal: this.events.signal });
    this.resize = new ResizeObserver(this.updateOffset);
    this.resize.observe(this);
    this.updateOffset();
  }

  disconnectedCallback() {
    this.events?.abort();
    this.events = null;
    this.resize?.disconnect();
    cancelAnimationFrame(this.frame);
    document.documentElement.style.removeProperty('--top-bar-height');
    document.documentElement.style.removeProperty('--top-bar-offset');
  }
}

class VeloraTopBarSlider extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.messages = [...this.querySelectorAll('.top-bar__message')];
    if (!this.messages.length) return;
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.index = 0;
    this.paused = false;
    this.hovered = false;
    this.focused = false;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.controls = this.querySelector('.top-bar__controls');
    this.controls.hidden = this.messages.length < 2;
    this.querySelector('[data-previous]').addEventListener('click', () => this.navigate(-1), options);
    this.querySelector('[data-next]').addEventListener('click', () => this.navigate(1), options);
    this.pauseButton = this.querySelector('[data-pause]');
    this.pauseButton.addEventListener('click', () => {
      this.paused = !this.paused;
      this.updatePlayback();
    }, options);
    this.addEventListener('mouseenter', () => { this.hovered = true; this.updatePlayback(); }, options);
    this.addEventListener('mouseleave', () => { this.hovered = false; this.updatePlayback(); }, options);
    this.addEventListener('focusin', () => { this.focused = true; this.updatePlayback(); }, options);
    this.addEventListener('focusout', event => {
      this.focused = this.contains(event.relatedTarget);
      this.updatePlayback();
    }, options);
    this.motion.addEventListener('change', () => this.updatePlayback(), options);
    document.addEventListener('visibilitychange', () => this.updatePlayback(), options);
    this.show(0);
    this.classList.add('is-enhanced');
    this.updatePlayback();
  }

  show(index) {
    this.index = (index + this.messages.length) % this.messages.length;
    this.messages.forEach((message, position) => {
      const active = position === this.index;
      message.classList.toggle('is-active', active);
      message.inert = !active;
    });
  }

  navigate(direction) {
    this.paused = true;
    this.show(this.index + direction);
    this.updatePlayback();
  }

  updatePlayback() {
    clearInterval(this.interval);
    const label = this.paused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel;
    this.pauseButton.textContent = label;
    this.pauseButton.setAttribute('aria-label', label);
    this.pauseButton.setAttribute('aria-pressed', String(this.paused));
    if (this.messages.length > 1 && !this.paused && !this.hovered && !this.focused && !this.motion.matches && !document.hidden) {
      this.interval = setInterval(() => this.show(this.index + 1), Number(this.dataset.interval) || 3000);
    }
  }

  disconnectedCallback() {
    clearInterval(this.interval);
    this.events?.abort();
    this.events = null;
    this.classList.remove('is-enhanced');
    this.messages?.forEach(message => { message.inert = false; });
    if (this.controls) this.controls.hidden = true;
  }
}

if (!customElements.get('velora-top-bar')) customElements.define('velora-top-bar', VeloraTopBar);
if (!customElements.get('velora-top-bar-slider')) customElements.define('velora-top-bar-slider', VeloraTopBarSlider);
